import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { enhanceProgram, formatCurrency, programKey } from '../utils/programs'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface Program {
  id?: string;
  key?: string;
  name?: string;
  teacher?: string;
  description?: string;
  goal?: number;
  annualBudget?: number;
  funded?: number;
  totalSpent?: number;
  [key: string]: unknown;
}

interface DonationButtonProps {
  status: string;
  onClick: () => void;
}

const determineKey = (program: Program, index: number): Program & { key: string } => {
  const enriched = enhanceProgram(program) as Program
  return {
    ...enriched,
    key: (enriched as any).key ?? `program-${index}`,
  }
}

const DonationButton = ({ status, onClick }: DonationButtonProps) => (
  <button
    onClick={onClick}
    disabled={status === 'loading'}
    className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-brick text-white font-semibold shadow-soft hover:bg-brickDark disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
  >
    {status === 'loading' ? 'Processing…' : 'Donate now'}
  </button>
)

const amountOptions = [25, 50, 100, 250]

export default function ProgramDetail() {
  const { programId } = useParams()
  const navigate = useNavigate()
  const { search } = useLocation()
  const [status, setStatus] = useState<string>('loading')
  const [program, setProgram] = useState<(Program & { key: string }) | null>(null)
  const [error, setError] = useState<string>('')
  const [donationStatus, setDonationStatus] = useState<string>('idle')
  const [donationError, setDonationError] = useState<string>('')
  const [amount, setAmount] = useState<number>(50)

  const donationOutcome = useMemo(() => {
    if (!search) return null
    const params = new URLSearchParams(search)
    const result = params.get('donation')
    if (!result) return null
    return result
  }, [search])

  useEffect(() => {
    if (!programId) {
      setStatus('missing')
      return
    }

    let ignore = false
    setStatus('loading')
    setProgram(null)
    setError('')

    api('programs')
      .then(({ programs }) => {
        if (ignore) return
        const items = Array.isArray(programs)
          ? programs.map((item, index) => determineKey(item, index))
          : []
        const match = items.find(item => item.id === programId || item.key === programId)
        if (!match) {
          setStatus('missing')
          return
        }
        setProgram(match)
        setStatus('ready')
      })
      .catch((err: Error) => {
        if (ignore) return
        setError(err?.message || 'Unable to load program')
        setStatus('error')
      })

    return () => {
      ignore = true
    }
  }, [programId])

  const goalValue = useMemo(() => {
    if (!program) return null
    const raw = program.goal ?? program.annualBudget
    const numberValue = Number(raw)
    return Number.isFinite(numberValue) ? numberValue : null
  }, [program])

  const fundedValue = useMemo(() => {
    if (!program) return null
    const raw = program.funded ?? program.totalSpent
    const numberValue = Number(raw)
    return Number.isFinite(numberValue) ? numberValue : null
  }, [program])

  const remainingValue = useMemo(() => {
    if (goalValue === null || fundedValue === null) return null
    const remainder = goalValue - fundedValue
    return remainder > 0 ? remainder : 0
  }, [goalValue, fundedValue])

  const progress = useMemo(() => {
    if (goalValue === null || goalValue <= 0 || fundedValue === null) return 0
    const pct = (fundedValue / goalValue) * 100
    return Math.max(0, Math.min(100, Math.round(pct)))
  }, [goalValue, fundedValue])

  const handleDonate = async () => {
    if (!program) return
    setDonationStatus('loading')
    setDonationError('')
    try {
      const keyValue = program.key ?? programKey(program as any)
      const successPath = keyValue ? `/programs/${keyValue}?donation=success` : '/programs?donation=success'
      const cancelPath = keyValue ? `/programs/${keyValue}?donation=cancelled` : '/programs?donation=cancelled'
      const SITE = window.location.origin
      const response = await fetch(`${CORE_API}/api/v1/checkout/donate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: Math.round(amount * 100),
          destination: 'classrooms',
          metadata: { programId: program.id, programKey: keyValue, programName: program.name },
          success_url: `${SITE}${successPath}`,
          cancel_url: `${SITE}${cancelPath}`,
        }),
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.error?.message || payload?.error || 'Unable to start checkout')
      }

      const url = payload?.data?.url ?? payload?.url
      if (url) {
        window.location.href = url
        return
      }

      throw new Error('Checkout session missing redirect URL')
    } catch (err) {
      setDonationError((err as Error)?.message || 'Something went wrong')
    } finally {
      setDonationStatus('idle')
    }
  }

  if (status === 'loading') {
    return (
      <section className="space-y-6">
        <div className="h-5 w-32 bg-white/60 rounded-full animate-pulse" />
        <div className="card h-64 bg-white/60 animate-pulse" />
      </section>
    )
  }

  if (status === 'error') {
    return (
      <section className="card space-y-4 bg-brick/10 text-brick">
        <div>
          <h2 className="text-xl font-semibold">We couldn’t load that program.</h2>
          <p className="text-sm text-cocoa/70">{error}</p>
        </div>
        <div>
          <button className="badge bg-white" onClick={() => navigate(-1)}>
            Go back
          </button>
        </div>
      </section>
    )
  }

  if (status === 'missing') {
    return (
      <section className="card space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-cocoa">Program not found</h2>
          <p className="text-sm text-cocoa/70">
            The program you're looking for may have been archived. Explore other initiatives on the programs page.
          </p>
        </div>
        <Link to="/programs" className="badge bg-brick text-white w-fit">
          Back to Programs
        </Link>
      </section>
    )
  }

  if (!program) return null

  const successMessage =
    donationOutcome === 'success'
      ? 'Thank you for your gift! Your donation is on its way to the classroom.'
      : donationOutcome === 'cancelled'
        ? 'Your donation was cancelled. You can try again anytime.'
        : null

  return (
    <section className="space-y-6">
      <nav className="text-sm text-cocoa/60">
        <Link to="/programs" className="hover:text-cocoa">
          ← All programs
        </Link>
      </nav>

      {successMessage && (
        <div className="card bg-mint/10 border border-mint/40 text-cocoa">
          {successMessage}
        </div>
      )}

      <article className="card space-y-6 bg-gradient-to-br from-shell via-white to-shell/60">
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-[0.2em] text-cocoa/60">Featured program</p>
          <h1 className="text-4xl font-bold text-cocoa">{program.name || 'Untitled Program'}</h1>
          {program.teacher && (
            <p className="text-cocoa/70">Led by {program.teacher}</p>
          )}
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          <dl className="rounded-2xl bg-white/70 p-5 md:col-span-2 space-y-4">
            <div className="grid gap-3 sm:grid-cols-3 text-sm">
              <div>
                <dt className="text-cocoa/60">Goal</dt>
                <dd className="text-xl font-semibold text-cocoa">
                  {formatCurrency(goalValue)}
                </dd>
              </div>
              <div>
                <dt className="text-cocoa/60">Funded</dt>
                <dd className="text-xl font-semibold text-cocoa">
                  {formatCurrency(fundedValue)}
                </dd>
              </div>
              <div>
                <dt className="text-cocoa/60">Remaining</dt>
                <dd className="text-xl font-semibold text-cocoa">
                  {formatCurrency(remainingValue)}
                </dd>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs font-medium text-cocoa/60 mb-1">
                <span>Progress</span>
                <span>{progress}% funded</span>
              </div>
              <div className="h-3 bg-white/70 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-brick to-mint rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </dl>

          <div className="rounded-2xl bg-white/80 p-5 space-y-5 border border-black/5">
            <div>
              <h2 className="text-lg font-semibold text-cocoa">Make a donation</h2>
              <p className="text-sm text-cocoa/70">
                Choose an amount to support this classroom. Donations go directly to the program’s budget.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {amountOptions.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setAmount(option)}
                  className={`badge px-4 py-2 ${
                    amount === option
                      ? 'bg-brick text-white shadow-soft'
                      : 'bg-shell text-cocoa hover:bg-white'
                  }`}
                >
                  {formatCurrency(option)}
                </button>
              ))}
            </div>

            <label className="block text-sm text-cocoa/70">
              Or enter an amount
              <input
                type="number"
                min="1"
                value={amount}
                onChange={event => setAmount(Number(event.target.value) || 0)}
                className="mt-2 w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-cocoa focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick focus-visible:ring-2 focus-visible:ring-brick/40"
              />
            </label>

            {donationError && (
              <div className="text-sm text-brick">{donationError}</div>
            )}

            <DonationButton status={donationStatus} onClick={handleDonate} />
          </div>
        </div>

        {program.description && (
          <section className="prose max-w-none text-cocoa/80">
            {program.description}
          </section>
        )}
      </article>
    </section>
  )
}
